"use client"

// Right-column result for the Lawn Lime Calculator: the shared peak frame
// (amount, pounds/bags reframe, one next step) and a details card with the
// split, source, and timing notes. All numbers come from the lib/lime-calculator
// result. The island places email capture between the two, then product picks.

import { ArrowDown, ArrowUp, Check, Divide, Info, Layers, Ruler } from "lucide-react"
import type { LimeCalculatorResult } from "@/lib/lime-calculator/calculator"
import { ResultSuccessCard, type ResultSuccessCardStat } from "@/components/tools/shared"
import { toolNavigationAnalytics } from "@/lib/analytics"
import { buildRelatedToolUrl } from "@/lib/result-tool-urls"
import { useImpressionEvent } from "@/hooks/useImpressionEvent"

interface LimeResultCardProps {
  result: LimeCalculatorResult
  lawnSizeSqft?: number
  zipCode?: string
}

export function LimeResultCard({ result, lawnSizeSqft, zipCode }: LimeResultCardProps) {
  const fertilizerHref = buildRelatedToolUrl("fertilizer-calculator", "lime-calculator", {
    grassType: result.grassDisplayName,
    lawnSize: lawnSizeSqft,
    zip: zipCode,
  })

  const impressionRef = useImpressionEvent<HTMLDivElement>("lime-calculator", undefined, {
    emit: () =>
      toolNavigationAnalytics.relatedToolsViewed({
        sourceTool: "lime-calculator",
        placement: "success_card",
        toolCount: 1,
      }),
  })

  const handleCtaClick = () => {
    toolNavigationAnalytics.relatedToolClicked({
      sourceTool: "lime-calculator",
      targetTool: "fertilizer-calculator",
      rank: 1,
      placement: "success_card",
      recommendationReason: "Feed once pH is on track",
      prefillFields: ["grass", lawnSizeSqft ? "size" : "", zipCode ? "zip" : ""].filter(Boolean).join(","),
    })
  }

  const cta = {
    href: fertilizerHref,
    label: `Plan fertilizer for my ${result.grassDisplayName}`,
    onClick: handleCtaClick,
  }

  if (result.inRange) {
    return (
      <div ref={impressionRef}>
        <ResultSuccessCard
          eyebrow="Your pH is already right"
          interpretation={
            <>
              {result.inRangeText} Re-test every 2-3 years, or after heavy rain seasons on sandy soil.
            </>
          }
          cta={cta}
        />
      </div>
    )
  }

  const isSulfur = result.direction === "sulfur"
  const totalLbs = result.totalLbs ?? 0
  const bags = result.bags ?? 1
  const bagLbs = result.bagLbs ?? 0
  const amendment = result.amendment?.toLowerCase() ?? (isSulfur ? "sulfur" : "lime")
  const phShift = `pH ${result.currentPh.toFixed(1)} to ${result.targetPh.toFixed(1)}`

  const stats: ResultSuccessCardStat[] = [
    { icon: Ruler, label: "Per 1,000 sq ft", value: `${result.lbsPer1000} lbs` },
    {
      icon: Layers,
      label: result.applications === 1 ? "Application" : "Applications",
      value: String(result.applications ?? 1),
    },
    { icon: isSulfur ? ArrowDown : ArrowUp, label: "pH change", value: phShift },
  ]

  return (
    <div ref={impressionRef}>
      <ResultSuccessCard
        stacked
        eyebrow={isSulfur ? "Lower your pH with sulfur" : "Raise your pH with lime"}
        heroValue={totalLbs.toLocaleString()}
        heroUnit="lbs"
        heroLabel={`of ${amendment} for your lawn`}
        reframe={{
          ariaLabel: "Show amount in pounds or bags",
          defaultKey: "lbs",
          options: [
            { key: "lbs", label: "Pounds" },
            { key: "bags", label: `${bagLbs} lb bags` },
          ],
          values: {
            lbs: { value: totalLbs.toLocaleString(), unit: "lbs", label: `of ${amendment} for your lawn` },
            bags: {
              value: String(bags),
              unit: bags === 1 ? "bag" : "bags",
              label: `of ${bagLbs} lb bags (${totalLbs.toLocaleString()} lbs total)`,
            },
          },
        }}
        interpretation={
          <>
            Spread {result.lbsPer1000} lbs of {amendment} per 1,000 sq ft
            {result.applications && result.applications > 1 ? " in total" : ""} to move your {result.grassDisplayName} lawn
            from {phShift}
            {result.applications && result.applications > 1
              ? `, split across ${result.applications} applications ${isSulfur ? "4 to 6 weeks" : "4 to 6 months"} apart.`
              : "."}
          </>
        }
        stats={stats}
        cta={cta}
      />
    </div>
  )
}

/** Split, lime-source, and timing notes under the peak frame. Nothing when pH is in range. */
export function LimeResultDetails({ result }: { result: LimeCalculatorResult }) {
  if (result.inRange) return null

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-elevation-2 sm:p-6">
      {result.splitNote && (
        <div className="mb-3.5 flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-3">
          <Divide className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
          <p className="text-[13px] leading-snug text-amber-800">{result.splitNote}</p>
        </div>
      )}

      {result.sourceReason && (
        <div className="mb-3.5 flex items-start gap-2.5 rounded-xl border border-green-200 bg-green-50 px-3.5 py-3">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-green-600" aria-hidden="true" />
          <p className="text-[13px] leading-snug text-green-800">{result.sourceReason}</p>
        </div>
      )}

      <h3 className="mb-2.5 text-base font-semibold text-gray-900">Timing and safety</h3>
      <div className="flex flex-col gap-2">
        {result.timingNotes.map((note, i) => (
          <div key={i} className="flex items-start gap-2.5">
            <Check className="mt-0.5 h-[15px] w-[15px] shrink-0 text-green-600" aria-hidden="true" />
            <span className="text-[13.5px] leading-normal text-gray-700">{note}</span>
          </div>
        ))}
      </div>
    </div>
  )
}
